import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { TeamCoverage } from '../lib/sources';

export type { TeamCoverage };

export type BriefingCategory = 'shipped' | 'still_building' | 'discussed';

// Changelog-style sub-classification within shipped/still_building buckets.
// Null for discussed items (research/analysis has no code-change type).
export type BriefingChangeType = 'feature' | 'fix' | 'improvement' | 'chore';

// ---- figure c: GitHub-backed diffstat (migration 096) ----
// Additive .nullish() enrichment from the MVP backend (lib/briefing/github-enrich.ts). Present only
// when a team GitHub integration exists and the conversation names a mirrored repo; the SPA renders
// the diffstat row only when present (degrade-to-absent until GitHub is connected).
export interface Diffstat {
  branchMerged?: string | null;
  baseBranch?: string | null;
  filesChanged?: number | null;
  additions?: number | null;
  deletions?: number | null;
  confidenceBand?: string | null;
}

// ---- PR-3 (brief-phase1 spec §2/§5): consolidated-story citations ----
// Hand-synced with the MVP's BriefCitationSchema (lib/dashboard/contracts/team-surfaces.ts).
// One citation per MEMBER NOTE of a consolidated story object — the sources an item arrived
// through. `url` is SOURCE-FIRST: the real artifact when derivable, else null, in which case
// conversationId → the note_doc viewer is the designed fallback (ai-chat/claude-code members
// never carry a url — those are owner-only platform links). `visibility` is the member's explicit
// class ('public' | 'audience_shared' | 'own_private'); own_private members only ever reach the
// PERSONAL brief because the team broadcast strips citations entirely in phase 1.
export interface BriefCitation {
  conversationId: string;
  site?: string | null;
  url?: string | null;
  label?: string | null; // the member note's headline (falls back to its topic)
  timestamp?: string | null;
  visibility?: string | null;
}

// Hand-synced with the MVP's BriefSourceRefSchema (ruling 7, popout build spec §5.5): one PR
// deep-link row per distinct `github_pr` entry across the card's members' stored refs, so a
// session note that MENTIONS a PR deep-links to it. Consumed by the source picker, which
// dedupes by URL against citation-derived targets (a ref-fused card's PR-note sibling can
// already carry the same URL). Personal brief only; nullish on legacy briefings.
export interface BriefSourceRef {
  kind: string; // "github_pr" today
  key: string; // `owner/repo#N`
  url: string; // https://github.com/<owner/repo>/pull/<N>
}

// Hand-synced with the MVP's StoredPopoutBodySchema (pregeneration build spec §2): the popout
// body composed at BRIEF time (composer v2, {shape, body, facts}) and stored under
// sections.popoutBodies[conversationId]. `facts` is earned — null/absent or 2-3 entries, never
// one; `from` is provenance-only and never rendered. composed:false = the deterministic
// fallback shipped for that card (still a real body).
export interface StoredPopoutBodyFact {
  text: string;
  from: string;
}
export interface StoredPopoutBody {
  shape: 'prose' | 'prose_and_facts';
  body: string;
  facts?: StoredPopoutBodyFact[] | null;
  composed: boolean;
}

export interface WhatChangedItem {
  memberName: string;
  memberRole: string;
  initial: string;
  category: BriefingCategory;
  changeType?: BriefingChangeType | null;
  headline: string;
  why?: string | null;
  site: string;
  topic: string;
  timestamp: string;
  conversationId: string;
  // figure c: GitHub-backed diffstat (migration 096). Nullish — absent until GitHub is connected.
  diffstat?: Diffstat | null;
  // Every contributor to this change. Single-author unless teammates' conversations were
  // grouped into one row by a shared team_project (then it's the author union, most-recent
  // first). Nullish: legacy briefings omit it → fall back to memberName. The surface renders
  // up to 2 avatars + a "+N" chip for larger groups.
  people?: string[] | null;
  // DEC-1 delivery (dec1-delivery-sweep T2): true when the source row was RESTRICTED (private=true)
  // and this recipient sees it only via the audience predicate. Kept in sync with the BFF contract
  // (WhatChangedItemSchema.fromRestricted). Slack-DM-only provenance — the SPA does NOT render it;
  // it's mirrored here per invariant #6 so the two hand-synced contract copies don't drift. Nullish:
  // absent on legacy briefings and on every whole-team (private=false) row.
  fromRestricted?: boolean | null;
  // Mock-alignment phase 2 (hand-synced with WhatChangedItemSchema): cross-array render rank
  // inside the ≤3-item summary band (0 = first; slot 0 reserved for company news when present),
  // and the news flag itself. Nullish on legacy briefings → changes-then-relevant order, no badge.
  summarySlot?: number | null;
  companyNews?: boolean | null;
  // PR-3 (hand-synced with WhatChangedItemSchema): the note's own stored summary — the dialog's
  // richer expand line, where `why` is the terse card-face one. Nullish on legacy briefings.
  summary?: string | null;
  // PR-3: one citation row per member note of this item's story object (see BriefCitation).
  // Personal brief ONLY — filterSectionsToShareable nulls it on every team-post item. Nullish on
  // legacy briefings → the dialog falls back to its single author/provenance row.
  citations?: BriefCitation[] | null;
  // PR-3: the artifact identity (`owner/repo#N`) when this item's object is ref-derived; nullish
  // for cluster/singleton objects. Stripped from the team broadcast alongside citations. The SPA
  // does NOT render it — mirrored here per invariant #6 so the two contract copies don't drift.
  objectKey?: string | null;
  // PR-3 (spec §5): resolved author user id beside memberName, from the attributing (never-email,
  // bug-516) member. Nullish for external authors / email rows / legacy briefings. Also not
  // rendered — same drift-parity reason as objectKey.
  authorUserId?: string | null;
  // Ruling 7 (hand-synced with WhatChangedItemSchema.sourceRefs): PR deep-link rows from the
  // members' stored refs — the source picker's GitHub rows. Nullish on legacy briefings.
  sourceRefs?: BriefSourceRef[] | null;
  // Legacy fields from briefings generated before the headline/category redesign.
  action?: string;
  text?: string;
}

export interface CategoryCounts {
  shipped: number;
  still_building: number;
  discussed: number;
}

export interface LeadSection {
  category: BriefingCategory;
  label: string;
  count: number;
  text: string;
}

export interface RelevantToYourWorkItem {
  authorName: string;
  topic: string;
  // All-cards subdesc (2026-08-10): the card face's CONTENT line — the note's own standfirst
  // (where_it_stands, else the summary fitted short), built in generate.ts. Ruling G2 bans a
  // relevance meta-line as a class; this says WHAT the note says, never why it is in the brief.
  // Hand-synced to generate.ts's RelevantToYourWorkSectionItem; nullish on legacy briefings.
  subdesc?: string | null;
  variant: 'solution' | 'similar_approach';
  confidenceBand: 'high' | 'medium' | 'low' | 'uncertain';
  conversationId: string;
  briefingItemId?: string;
  // Provenance (where/when): capture source + timestamp of the teammate's conversation.
  site?: string | null;
  timestamp?: string | null;
  // relevancy-v2 pass-through (MVP lib/briefing/relevance.ts RelevanceItem): the scorer's routed
  // value/version, hand-synced to match generate.ts's RelevantToYourWorkSectionItem.
  value?: number | null;
  scorerVersion?: string | null;
  // Mock-alignment phase 2 — same pair as WhatChangedItem (summary-band slot rank + news flag).
  summarySlot?: number | null;
  companyNews?: boolean | null;
  // PR-3 (spec §2 D-8): when this card is a ref-identity object's home it carries that object's
  // citation rows. Hand-synced with generate.ts's RelevantToYourWorkSectionItem rather than a Zod
  // schema — the contract types `relevant` loosely as z.unknown(), so generate.ts is the shape of
  // record here. Personal brief only; nullish on legacy briefings.
  citations?: BriefCitation[] | null;
  // Ruling 7 — same rows as WhatChangedItem.sourceRefs, same hand-sync-to-generate.ts basis.
  sourceRefs?: BriefSourceRef[] | null;
}

// ---- figure a: GitHub-backed branch divergence + conflict hunk (migration 096) ----
// conflictHunk carries the conflicting FILE only (from the open PR's changed files). The line
// content it used to declare — lineNumber/yourLine/theirLine — was never derivable from GitHub REST:
// the enricher set all three to null unconditionally and only a fixture ever filled them, so the
// renderer promised a diff the pipeline could not deliver. Deleted 2026-07-31 (phase-B walk D15) on
// both sides of the contract. Present only when a collision names a mirrored repo with an open PR.
export interface ConflictHunk {
  filePath: string;
}

export interface BranchDivergence {
  branchYours?: string | null;
  branchTheirs?: string | null;
  aheadBy?: number | null;
  behindBy?: number | null;
  mergeableState?: string | null;
  conflictHunk?: ConflictHunk | null;
}

export interface NeedsInputItem {
  tag: string;
  title: string;
  detail: string;
  severity?: 'collision' | 'related' | 'coarse';
  sharedEntities?: string[];
  conversationId: string;
  secondaryConversationId?: string | null;
  briefingItemId?: string;
  // Provenance (who/when): display names of the colliding parties (typically two)
  // + most-recent activity timestamp across the two conversations.
  authors?: string[] | null;
  timestamp?: string | null;
  // figure a: GitHub-backed branch divergence + conflict hunk (migration 096). Nullish — absent
  // until GitHub is connected; rendered only on collisions that name a mirrored repo with an open PR.
  branchDivergence?: BranchDivergence | null;
}

export interface KnowledgeCapturedItem {
  type: 'convention' | 'discovery' | 'preference';
  text: string;
  conversationId: string;
  entityTags: string[];
  // Provenance (where/who/when): capture source, author display name, capture timestamp.
  site?: string | null;
  authorName?: string | null;
  timestamp?: string | null;
}

// ---- figure b: opinion-lean meter (sharedDecisions) ----
// Mirrors the MVP backend contract for the team daily briefing. The backend always sends
// sharedDecisions: [] when there are no forming decisions; legacy briefings stored before this
// field existed omit it entirely, so the SPA reads it defensively (?? []).
export type StanceLean =
  | 'strongly_for'
  | 'leaning_for'
  | 'neutral_undecided'
  | 'leaning_against'
  | 'strongly_against'
  | 'no_signal';

export type EvidenceStrength = 'explicit' | 'implied' | 'weak';

export interface TeammateStance {
  memberName: string;
  memberRole: string;
  initial: string;
  lean: StanceLean;
  positionSummary: string;
  supportingQuote?: string | null;
  evidenceStrength?: EvidenceStrength | null;
  conversationId: string;
  confidence: number;
  confidenceBand: string;
}

export interface SharedDecision {
  decisionQuestion: string;
  poleFor: string;
  poleAgainst: string;
  sharedEntities: string[];
  teamProjectName?: string | null;
  stances: TeammateStance[];
  conversationId: string;
  // When the team last moved on this decision (newest contributing note's content time). Optional —
  // briefings generated before it shipped omit it, and those cards must keep rendering undated.
  timestamp?: string | null;
}

// ---- figure e: project progress (projectProgress) ----
// Mirrors the MVP backend contract. The backend always sends projectProgress: [] when there are no
// in-flight projects; legacy briefings omit it (read defensively). etaLabel is a cadence SIGNAL
// ("active this week" / "no activity in 6 days"), never a date; targetDate is GitHub-only and unused.
export type ProgressBand =
  | 'not_started'
  | 'early'
  | 'in_progress'
  | 'nearing_completion'
  | 'shipped';

export type EtaSignal = 'stalled' | 'active' | 'accelerating';

export interface ProjectProgress {
  projectId?: string | null;
  projectName: string;
  progressBand: ProgressBand;
  progressPct: number;
  pctIsEstimated: boolean;
  milestoneNarrative: string;
  currentMilestone?: string | null;
  lastShippedMilestone?: string | null;
  blockers: string[];
  etaSignal?: EtaSignal | null;
  etaLabel?: string | null;
  targetDate?: string | null;
  // When the project last MOVED — backward-looking, unlike targetDate above (forward-looking, unused).
  // Optional: older briefings omit it.
  timestamp?: string | null;
  // Ruling C1 (2026-08-02, bug-614) — the progress card's IN-APP exit: the note that set `timestamp`
  // (the newest entry in the timeline this narrative was synthesized from). A progress narrative is
  // prose over several notes, so "the note" is the one that moved it last. ADDITIVE + optional:
  // absent on every briefing generated before this field shipped → no in-app exit, as today.
  // Declared here to keep the SPA in step with the MVP's ProjectProgressSchema in the same change
  // (two-repo contract invariant); the legacy v1 renderer below draws no exits yet.
  conversationId?: string | null;
  confidenceBand: string;
}

// ---- v2 sections: briefing-standardization (migration 119+)
// New v2 briefing structure. Absent on legacy rows; BriefSurface branches on `version`
// and renders legacy rows via TeamTodaySurface.

export type BriefTaskStream =
  | 'conflict'
  | 'overlap'
  | 'meeting_prep'
  | 'linear'
  | 'notion'
  | 'github_review'
  | 'invite'
  | 'jira'
  | 'focus'; // "Today's Focus" synthesized workstream task (MVP lib/briefing/standard/focus.ts)

// Calibrated confidence band of a conflict group (MVP lib/briefing/bands.ts). Conflict-stream only.
export type BriefTaskConfidenceBand = 'high' | 'medium' | 'low' | 'uncertain';

export interface BriefTask {
  taskId: string;
  stream: BriefTaskStream;
  title: string;
  detail?: string | null;
  value: number;
  treatment?: string | null;
  scorerVersion?: string | null;
  urgency?: number | null;
  dueLabel?: string | null;
  conversationId?: string | null;
  externalUrl?: string | null;
  briefingItemId?: string | null;
  spotlight?: boolean;
  // Conflict-stream ONLY: drives the SPA resolution gate (webapp-actions §6.6 / R1). high/medium open
  // the full "pick a side" modal; low/uncertain get the lightweight "is this even a conflict?" eval-only
  // prompt. Nullish for every other stream (and legacy rows). See ConflictResolveModal.isEvalOnlyBand.
  band?: BriefTaskConfidenceBand | null;
  // Conflict-stream ONLY: every member edge's briefingItemId for a GROUPED conflict card (a connected
  // component of contradicting decisions merged into "one argument", MVP task-streams.ts). Includes the
  // representative (briefingItemId). Resolving the card must settle EVERY member, or the un-flipped
  // siblings re-form the identical card next brief (spec O1 "group-dismiss fan-out"). Empty for
  // single-edge conflicts and every non-conflict stream. Present in the BFF payload; hand-synced here.
  memberBriefingItemIds?: string[] | null;
  personalOnly?: boolean;
  // Conflict-stream ONLY: did this conflict earn one of the brief's few priority slots? The server
  // escalates at most 2 high-band conflicts (strongest first) and flags the rest `false` — they still
  // ride in `tasks` and still render in full on the Actions page, they just don't crowd the brief's
  // priority column. NULLISH IS LEGACY: a payload without the field predates the cap, so absent must
  // render exactly as before (every conflict on the brief). Only an explicit `false` demotes.
  escalated?: boolean | null;
  // Ruling-7 refs widened to tasks (popout spec §12, hand-synced with BriefTaskSchema.sourceRefs):
  // PR deep-link rows for the task popout's source picker. Nullish: conflict/ungrounded tasks,
  // ref-less notes, and every legacy stored row.
  sourceRefs?: BriefSourceRef[] | null;
}

export interface BriefTopItem {
  section: 'tasks' | 'your_day' | 'around' | 'pulse';
  title: string;
  conversationId?: string | null;
  externalUrl?: string | null;
}

export interface BriefMasthead {
  headline: string;
  standfirst?: string | null;
  topItem?: BriefTopItem | null;
}

// webapp-actions A5 (§2.0#9): the relevance scorer's suppression receipt — how many candidates it weighed
// and cut, tallied by WHY (aggregate counts only, no item identities). `reason` keys mirror the MVP
// SUPPRESS_REASONS (below_relevance / low_value / abstained / near_duplicate / over_budget).
export interface BriefSuppression {
  count: number;
  reasons: Array<{ reason: string; count: number }>;
}

// D5/D14 (phase-B B2, hand-synced with the MVP's BriefAroundTailItemSchema): one row of the General
// section's expanded popout. Flat by design — a row is a heading + body + chip, not a card — and it
// carries the same fields the band card renders from, so the popout composes its chip out of the
// vocabulary the surface already ships (catLabel / confidenceLabel) rather than new strings.
export interface BriefAroundTailItem {
  kind: 'change' | 'relevant';
  conversationId: string;
  title: string;
  body?: string | null;
  site?: string | null;
  authorName?: string | null;
  timestamp?: string | null;
  category?: BriefingCategory | null;             // change rows
  changeType?: BriefingChangeType | null;         // change rows
  confidenceBand?: RelevantToYourWorkItem['confidenceBand'] | null;  // relevant rows
}

export interface BriefAround {
  changes?: WhatChangedItem[];
  changesCollapsedCount?: number;
  relevant?: RelevantToYourWorkItem[];
  relevantCollapsedCount?: number;
  // A5: additive — the scorer's suppression receipt (count + per-reason tally). Nullish; absent on legacy
  // briefings (renders as no receipt). Deliberately separate from relevantCollapsedCount (different meaning).
  suppressed?: BriefSuppression | null;
  // D5/D14 (B2): everything the General band cut that appears NOWHERE ELSE on the page — the list the
  // "N more" button opens, and the list whose LENGTH is that N (never a separate subtraction, so the
  // number can't promise more than the sheet shows). ABSENT (undefined) on a briefing generated before
  // this field: the surface then falls back to the old "+N more · on your Map" receipt, so archived
  // briefs keep rendering exactly as they did. `[]` means the honest answer is "nothing" — no receipt.
  // changesCollapsedCount/relevantCollapsedCount stay on the wire for Slack and legacy rows.
  tail?: BriefAroundTailItem[] | null;
}

// ---- Mock-alignment phase 2: per-source distillation panels (hand-synced with the MVP's
// BriefSourcePanelSchema family). The band/task 3-caps route overflow here; each drill-in item
// deep-links to the REAL source (Slack thread permalink / Gmail thread / GitHub PR). ----
export type BriefSourcePanelSource = 'email' | 'slack' | 'github' | 'overview';

export interface BriefSourceTopicItem {
  title: string;
  note?: string | null; // one-line provenance ("From Jordan")
  tag?: string | null;  // closed-enum triage tag (action/question/decision/reference/update/fyi)
  url?: string | null;  // real source deep link — the §8 traceability verb
  conversationId?: string | null; // Terum note PK → in-app "Open the note"
  // PR-3 (hand-synced with BriefSourceTopicItemSchema): the same artifact's other copies —
  // email/slack/github siblings of this item's story object. Only ever present when the object
  // has >1 member (a lone member adds nothing beyond the item's own url/conversationId), so the
  // dialog renders it as an "also in …" tail on the provenance line, not as extra rows.
  citations?: BriefCitation[] | null;
}

export interface BriefSourceTopic {
  id: string;
  title: string;
  preview?: string | null;
  description?: string | null;
  count?: number;
  split?: string | null; // computed tag tally ("2 action · 1 reference"), never LLM prose
  items?: BriefSourceTopicItem[];
}

export interface BriefSourcePanel {
  source: BriefSourcePanelSource;
  topics?: BriefSourceTopic[];
}

export interface WeatherTile {
  tempC?: number | null;
  tempMaxC?: number | null;
  tempMinC?: number | null;
  weatherCode?: number | null;
  locationLabel?: string | null;
}

export interface StandupEntry {
  memberName: string;
  memberRole?: string | null;
  initial?: string | null;
  sentence: string;
  conversationIds?: string[];
}

export type MeetingTriageCategory = 'action' | 'attend' | 'report_candidate';

export interface MeetingBriefItem {
  eventId: string;
  title: string;
  startAt?: string | null;
  endAt?: string | null;
  when?: string | null;
  day?: 'today' | 'tomorrow' | null;
  attendeesSummary?: string | null;
  teammateAttendees?: string[];
  location?: string | null;
  meetingUrl?: string | null;
  htmlLink?: string | null;
  category?: MeetingTriageCategory | null;
  needsAction?: boolean;
  importance?: number | null;
  relevanceSimilarity?: number | null;
  relatedWork?: string[];
  why?: string | null;
  // webapp-actions §6.5 (A3 row half): the meeting's agenda (MVP calendar_events.description, capped).
  // Nullish — absent on legacy rows and meetings with no description.
  agenda?: string | null;
}

export interface BriefingSections {
  summaryStats: Array<{ label: string; value: string; tone: string }>;
  categoryCounts?: CategoryCounts | null;
  whatChanged: WhatChangedItem[];
  needsYourInput: NeedsInputItem[];
  knowledgeCaptured: KnowledgeCapturedItem[];
  relevantToYourWork: RelevantToYourWorkItem[];
  // figure b / figure e (migration 096 era). Optional: legacy briefings stored before these
  // fields existed omit them; the surface reads them with `?? []`.
  sharedDecisions?: SharedDecision[];
  projectProgress?: ProjectProgress[];
  leadSections?: LeadSection[];
  lead: string | null;
  // v2 (briefing-standardization). Absent on legacy rows — BriefSurface
  // branches on `version` and renders legacy rows via TeamTodaySurface.
  version?: number;
  // masthead still ARRIVES on every v2 payload (the Slack DM renders headline/standfirst; topItem is
  // mechanical) but the redesigned renderer ignores it — the masthead is static since the 2026-07-26
  // mock-alignment pass. bandNarratives/bandHooks were removed from the payload in the same pass.
  masthead?: BriefMasthead | null;
  tasks?: BriefTask[];
  tasksCollapsedCount?: number;
  // Phase-2 caps (bug-537, hand-synced): count of server-cut non-conflict tasks. The Slack DM folds
  // it into its "+N more" line; since the panels went per-source-only (2026-07-27) the web folds it
  // into the tasks column's "below today's threshold" tail.
  tasksOverflowCount?: number | null;
  // How many conflicts the server demoted off the brief's priority column (escalated:false). They are
  // NOT cut — every one still rides in `tasks` and renders on Actions — so the brief owes the reader a
  // count and a way through, never silence. Nullish on legacy payloads (no cap ran, nothing demoted).
  conflictsDemotedCount?: number | null;
  around?: BriefAround | null;
  standup?: StandupEntry[];
  weather?: WeatherTile | null;
  quietDay?: boolean;
  sendScore?: number | null;
  meetings?: MeetingBriefItem[];
  // Ghost Phase B (§3.1): team-wide connected/observed counts + named subset. Non-null only for a solo
  // recipient; drives one honest coverage line in the briefing. Absent on legacy rows.
  coverage?: TeamCoverage | null;
  // Mock-alignment phase 2: per-source distillation panels. Nullish — absent on legacy briefings
  // (no panels section renders). Personal brief only; the MVP strips it from team broadcasts.
  // Per-source only since 2026-07-27 (email/slack/github; legacy "overview" rows don't render).
  sourcePanels?: BriefSourcePanel[] | null;
  // Prior-brief receipt (backend rails 2026-07-27, hand-synced to PriorActionsSchema): what
  // happened to the recipient's LAST brief. Absent on legacy rows / the first brief. Personal
  // only — the MVP strips it from team broadcasts.
  priorActions?: PriorActions | null;
  // Pregenerated popout bodies (pregeneration build spec §2, keying widened §12; hand-synced
  // with BriefingSectionsSchema.popoutBodies): keyed by conversationId for note-anchored cards,
  // or `panel:<source>:<topicId>` for panel topics. ADDITIVE + nullish — absent on legacy
  // briefs; a card whose key is missing falls back to the per-click POST compose
  // (useBriefPopoutBody).
  popoutBodies?: Record<string, StoredPopoutBody> | null;
}

export interface PriorActions {
  /** The prior brief's date (YYYY-MM-DD). */
  date: string;
  actedCount?: number;
  dismissedCount?: number;
  pendingCount?: number;
  totalSurfaced?: number;
  settledSinceCount?: number;
}

export interface TeamTodaySurfaceData {
  status: 'ready' | 'empty' | 'not_generated' | 'no_team';
  data: BriefingSections | null;
  generated_at: string | null;
  viewed_at: string | null;
  is_previous?: boolean;
  // Lane-0 D1 (2026-08-12, hand-synced with the MVP's TeamTodaySurfaceDataSchema): true while the
  // server's auto-kicked background rebuild is presumed running (it CAS-stamps a once-a-day kick
  // and reports in-flight for up to 10 minutes). Drives the poll-until-swap below and the D3
  // banner's "building today's" state in BriefSurface. Absent on no_team/not_generated responses.
  refresh_in_flight?: boolean;
}

export function useTeamTodaySurface() {
  const query = useQuery({
    queryKey: ['surfaces', 'team-today'],
    queryFn: () => apiFetch<TeamTodaySurfaceData>('/api/surfaces/team/today'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // bug-716 (SPA half): the app-wide default is refetchOnWindowFocus:false, and this query's key
    // carries no date — so an installed-PWA window left open across the day rollover (or across the
    // noon regeneration) kept serving yesterday's payload forever; nothing ever refired it. The
    // today surface is the one query whose whole meaning is "current", so it alone opts into
    // focus-refetch. staleTime still bounds it: at most one refetch per 5 minutes, and only when
    // the reader returns to the window — never a swap mid-read.
    refetchOnWindowFocus: true,
    // Lane-0 D1 (2026-08-12): poll-until-swap. While the server reports an auto-kicked rebuild in
    // flight, refetch every 15s so the fresh brief swaps in without user action. The server stops
    // reporting in-flight once the rebuild lands (fresh row → not stale) or its 10-minute window
    // lapses (stuck → the D3 banner's Build-now state), which stops the poll — no client timer to
    // leak. Off everywhere else, matching the app-wide no-polling default.
    refetchInterval: (query) => (query.state.data?.refresh_in_flight ? 15_000 : false),
  });
  return { ...query, data: query.data ?? undefined };
}
